import { type MouseEvent, useCallback, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { Cookie } from '@/utils/cookie';

import { type TeamTabValue } from './jsm-portal-playground-data';

export type PlaygroundScreenNavigatedParams = {
	team_type?: string;
	screen_id: string;
	interaction_mode?: string;
};

export type TextFieldBlurredParams = {
	screenId: string;
	elementId: string;
	actionSubjectId?: string;
	hasChanged: boolean;
	inputLength: number;
	teamType?: string;
	extraAttributes?: Record<string, unknown>;
};

export type ComponentClickedAttributes = {
	trackEventName: 'clicked-mock-ui';
	eventContainer: 'jsm-playground';
	team_type: string;
	screen_id: string;
	element_type: string;
	element_id: string;
	label?: string;
	page_canonical_url?: string;
	page_path?: string;
	loginUserDetected?: string;
	[key: string]: unknown;
};

const getCanonicalUrl = (): string => {
	if (typeof document === 'undefined') {
		return '';
	}
	const canonical = document.querySelector('link[rel="canonical"]')?.getAttribute('href');
	if (canonical) {
		return canonical;
	}
	const [urlWithoutHash] = window.location.href.split('#');
	return urlWithoutHash ?? '';
};

const getPagePath = (): string => {
	if (typeof window !== 'undefined') {
		return window.location.pathname;
	}
	return '';
};

const getLoginUserDetected = (): string => {
	if (typeof document === 'undefined') {
		return 'false';
	}
	return Cookie.readCookie('login_user_detected') === 'true' ? 'true' : 'false';
};

const sanitizeId = (raw: string): string => {
	return raw
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '');
};

const resolveElementType = (target: HTMLElement): string => {
	if (
		target.getAttribute('role') === 'checkbox' ||
		(target instanceof HTMLInputElement && target.type === 'checkbox')
	) {
		return 'checkbox';
	}
	if (target.getAttribute('role') === 'tab') {
		return 'tab';
	}
	if (target.getAttribute('role') === 'button' || target instanceof HTMLButtonElement) {
		return 'button';
	}
	if (
		target instanceof HTMLInputElement ||
		target instanceof HTMLTextAreaElement ||
		target instanceof HTMLSelectElement
	) {
		return 'input';
	}
	if (target.getAttribute('role') === 'link' || target instanceof HTMLAnchorElement) {
		return 'link';
	}
	return target.tagName.toLowerCase();
};

const resolveElementId = (target: HTMLElement): string => {
	const explicitId =
		target.getAttribute('data-element-id') || target.getAttribute('data-testid') || target.id;

	if (explicitId) {
		return sanitizeId(explicitId);
	}

	const ariaLabel = target.getAttribute('aria-label');
	if (ariaLabel) {
		return sanitizeId(ariaLabel);
	}

	const text = target.textContent?.trim();
	if (text && text.length <= 40) {
		return sanitizeId(text);
	}

	return 'component';
};

const resolveLabel = (target: HTMLElement): string | undefined => {
	const labelAttr = target.getAttribute('data-label') || target.getAttribute('aria-label');
	if (labelAttr) {
		return labelAttr.trim();
	}

	const text = target.textContent?.trim();
	if (text && text.length > 0 && text.length <= 80) {
		return text;
	}

	return undefined;
};

const INTERACTIVE_SELECTOR = [
	'button',
	'a[href]',
	'input:not([disabled])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'label',
	'[role="button"]',
	'[role="checkbox"]',
	'[role="tab"]',
	'[role="link"]',
	'[data-clickable="true"]',
].join(', ');

export const isUntrustedClick = (
	event?: MouseEvent<HTMLElement> | globalThis.MouseEvent,
): boolean => {
	if (!event) {
		return false;
	}
	const isUntrustedOverride =
		(event as unknown as { isTrustedOverride?: boolean }).isTrustedOverride === false ||
		(event as unknown as { nativeEvent?: { isTrustedOverride?: boolean } }).nativeEvent
			?.isTrustedOverride === false;
	if (isUntrustedOverride) {
		return true;
	}
	return (
		typeof event.isTrusted === 'boolean' && !event.isTrusted && process.env['NODE_ENV'] !== 'test'
	);
};

export const useJsmPlaygroundAnalytics = (
	defaultTeamType: string | TeamTabValue = 'it_support',
) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const lastClickRef = useRef<{ id: string; time: number } | null>(null);

	const firePlaygroundScreenNavigated = useCallback(
		({
			team_type = defaultTeamType.replace(/-/g, '_'),
			screen_id,
			interaction_mode = 'manual_click',
		}: PlaygroundScreenNavigatedParams) => {
			createAnalyticsEvent({
				actionSubject: 'playgroundScreen',
				action: 'navigated',
				eventType: 'ui',
				source: 'playground',
				attributes: {
					team_type,
					screen_id,
					interaction_mode,
				},
			}).fire();
		},
		[createAnalyticsEvent, defaultTeamType],
	);

	const fireTextFieldBlurred = useCallback(
		({
			screenId,
			elementId,
			actionSubjectId = elementId,
			hasChanged,
			inputLength,
			teamType = defaultTeamType.replace(/-/g, '_'),
			extraAttributes,
		}: TextFieldBlurredParams) => {
			const loginCookie =
				typeof window !== 'undefined' ? Cookie.readCookie('login_user_detected') : null;
			const loginUserDetected = loginCookie === 'true' ? 'true' : 'false';

			createAnalyticsEvent({
				actionSubject: 'textField',
				action: 'blurred',
				actionSubjectId,
				eventType: 'ui',
				source: 'jsm-playground',
				attributes: {
					trackEventName: 'blurred-mock-input',
					eventContainer: 'jsm-playground',
					team_type: teamType,
					screen_id: screenId,
					element_id: elementId,
					hasChanged,
					inputLength,
					...(typeof window !== 'undefined'
						? {
								page_canonical_url: window.location.href,
								page_path: window.location.pathname,
							}
						: {}),
					loginUserDetected,
					...extraAttributes,
				},
			}).fire();
		},
		[createAnalyticsEvent, defaultTeamType],
	);

	const handlePreviewClick = useCallback(
		(event: MouseEvent<HTMLElement> | globalThis.MouseEvent, screenId = 'request_queue') => {
			// Do NOT fire when automated / scripted animations click components.
			if (isUntrustedClick(event)) {
				return;
			}

			const target = event.target as HTMLElement | null;
			if (!target) {
				return;
			}

			const interactiveElement = target.closest(INTERACTIVE_SELECTOR) as HTMLElement | null;
			if (!interactiveElement) {
				return;
			}

			const elementType = resolveElementType(interactiveElement);
			const elementId = resolveElementId(interactiveElement);

			// Prevent duplicate rapid clicks on the same component (e.g. accidental double-clicks or clicking while view transitions out)
			const now = Date.now();
			if (
				lastClickRef.current &&
				lastClickRef.current.id === elementId &&
				now - lastClickRef.current.time < 400
			) {
				return;
			}
			lastClickRef.current = { id: elementId, time: now };

			const label = resolveLabel(interactiveElement);

			const canonicalUrl = getCanonicalUrl();
			const pagePath = getPagePath();
			const loginUser = getLoginUserDetected();
			const teamType = defaultTeamType.replace(/-/g, '_');

			createAnalyticsEvent({
				actionSubject: 'component',
				action: 'clicked',
				eventType: 'ui',
				source: 'jsm-playground',
				attributes: {
					trackEventName: 'clicked-mock-ui',
					eventContainer: 'jsm-playground',
					team_type: teamType,
					screen_id: screenId,
					element_type: elementType,
					element_id: elementId,
					...(label ? { label } : {}),
					...(canonicalUrl ? { page_canonical_url: canonicalUrl } : {}),
					...(pagePath ? { page_path: pagePath } : {}),
					loginUserDetected: loginUser,
				},
			}).fire();
		},
		[createAnalyticsEvent, defaultTeamType],
	);

	return {
		createAnalyticsEvent,
		firePlaygroundScreenNavigated,
		fireTextFieldBlurred,
		handlePreviewClick,
	};
};
